import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import api from '../api'; // Use the centralized api instance

const GenieContext = createContext();

export const useGenies = () => useContext(GenieContext);

export const GenieProvider = ({ children }) => {
  const [genies, setGenies] = useState([]);
  const [selectedGenie, setSelectedGenie] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // True while a chat question is being processed — disables genie/client switching
  const [isProcessing, setIsProcessing] = useState(false);
  const isFetching = useRef(false);
  // Track if auth has completed so clientChanged handler knows whether to fetch or defer
  const isAuthReady = useRef(false);
  // If clientChanged fires before auth is ready, store the pending client here.
  // Use UNDEFINED as sentinel meaning "nothing deferred yet" (null = explicitly no client).
  const pendingClientRef = useRef(undefined);
  // Monotonically increasing counter: each fetch call gets a unique seq number.
  // When the response arrives we discard it if a newer fetch was already started.
  const fetchSeqRef = useRef(0);
  const latestSeqRef = useRef(0);
  // Mirror of selectedGenie state accessible inside the stable fetchGenies callback
  // without creating a stale closure. Updated in sync with setSelectedGenie.
  const selectedGenieRef = useRef(null);

  // Wrap setSelectedGenie so we always keep the ref in sync
  const setSelectedGenieAndRef = useCallback((genie) => {
    selectedGenieRef.current = genie;
    setSelectedGenie(genie);
  }, []);

  // Fetch genies based on selected client.
  // Returns a promise that resolves once the fetch (and any stale-result check) completes.
  const fetchGenies = useCallback(async (clientName = null) => {
    // Assign a sequence number to this request
    fetchSeqRef.current += 1;
    const mySeq = fetchSeqRef.current;
    latestSeqRef.current = mySeq;

    setIsLoading(true);
    
    try {
      const url = clientName ? `/api/genies?client=${encodeURIComponent(clientName)}` : '/api/genies';
      
      const geniesResponse = await api.get(url);

      // Discard result if a newer fetch was requested while this one was in flight
      if (mySeq < latestSeqRef.current) {
        return;
      }

      const fetchedGenies = geniesResponse.data;
      setGenies(fetchedGenies);

      if (fetchedGenies.length > 0) {
        // Preserve the user's current genie selection if it still exists in the new list.
        // Only fall back to the first genie when:
        //   1. No genie was previously selected, OR
        //   2. The previously selected genie is no longer in the refreshed list
        //      (e.g., client changed and the genie belongs to a different client).
        const currentId = selectedGenieRef.current?.id;
        const stillExists = currentId && fetchedGenies.some(g => g.id === currentId);
        if (stillExists) {
          // Re-point ref to the freshly fetched object so data is up-to-date
          const refreshed = fetchedGenies.find(g => g.id === currentId);
          setSelectedGenieAndRef(refreshed);
        } else {
          setSelectedGenieAndRef(fetchedGenies[0]);
        }
      } else {
        setSelectedGenieAndRef(null);
      }
      
    } catch (error) {
      if (mySeq < latestSeqRef.current) return; // stale — ignore error too
      console.error('[GENIE CONTEXT] ⚠️ Genies fetch failed:', error);
      setGenies([]);
      setSelectedGenieAndRef(null);
    } finally {
      // Only clear loading if this is still the latest request
      if (mySeq >= latestSeqRef.current) {
        setIsLoading(false);
      }
    }
  }, [setSelectedGenieAndRef]);

  // Initial data fetch (user + genies)
  useEffect(() => {
    const fetchData = async () => {
      if (isFetching.current) return;
      isFetching.current = true;
      
      // Fetch user first (critical for auth)
      try {
        const userResponse = await api.get('/api/user');
        setCurrentUser(userResponse.data);
        setIsAuthenticated(true);
        
        // Mark auth as ready BEFORE dispatching event or fetching genies
        isAuthReady.current = true;
        
        // Notify other components that user data is loaded
        window.dispatchEvent(new Event('authStateChanged'));
      } catch (error) {
        console.error('[GENIE CONTEXT] User fetch failed:', error);
        if (error.response?.status === 401) {
          // api.js interceptor will redirect to tt-auth login
          console.error('[GENIE CONTEXT] ❌ 401 Unauthorized — tt-auth redirect in progress');
          setIsAuthenticated(false);
          setCurrentUser(null);
          window.dispatchEvent(new Event('authStateChanged'));
        }
        isFetching.current = false;
        return;
      }
      
      // After auth, check if a clientChanged event was deferred (fired before auth completed).
      // pendingClientRef uses undefined as sentinel = "nothing deferred yet".
      // null means clientChanged fired explicitly with no client (client cleared).
      const pendingClient = pendingClientRef.current;
      pendingClientRef.current = undefined; // reset sentinel

      // Determine which client to fetch for:
      //   - pendingClient !== undefined  → a clientChanged was deferred before auth
      //   - otherwise                   → check localStorage (Navbar may have set it already)
      const clientToFetch = pendingClient !== undefined
        ? pendingClient
        : localStorage.getItem('selectedClient'); // may be null if no client stored

      if (clientToFetch) {
        setSelectedClient(clientToFetch);
        await fetchGenies(clientToFetch);
      } else {
        // No client context at all — fetch all genies as fallback
        await fetchGenies(null);
      }
      isFetching.current = false;
    };

    fetchData();
  // fetchGenies is stable (useCallback with no deps), safe to include
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for client selection changes — bound once, never re-bound.
  // fetchGenies, isAuthReady, and pendingClientRef are all stable refs so no
  // stale closures and no need to re-bind on every render.
  useEffect(() => {
    const handleClientChange = () => {
      const storedClient = localStorage.getItem('selectedClient');
      setSelectedClient(storedClient);

      if (!isAuthReady.current) {
        // Auth hasn't completed yet — defer the fetch; fetchData will pick up pendingClientRef
        pendingClientRef.current = storedClient;
        return;
      }

      // fetchGenies assigns its own sequence number internally — no external token needed.
      // If another clientChanged fires before this response arrives, the stale result is
      // automatically discarded via the seq counter.
      fetchGenies(storedClient);
    };

    // Listen for admin genie map changes — refresh genies immediately after admin
    // adds or removes a genie mapping so the /genies page reflects changes in real time.
    const handleGenieMapChanged = () => {
      if (!isAuthReady.current) return; // Auth not ready yet — skip; initial fetch will handle it
      const storedClient = localStorage.getItem('selectedClient');
      fetchGenies(storedClient);
    };

    window.addEventListener('clientChanged', handleClientChange);
    window.addEventListener('genieMapChanged', handleGenieMapChanged);

    // Sync selectedClient state with localStorage on mount only.
    // Do NOT trigger a genie fetch here — fetchData handles the initial fetch after auth.
    const storedClient = localStorage.getItem('selectedClient');
    if (storedClient) {
      setSelectedClient(storedClient);
    }
    
    return () => {
      window.removeEventListener('clientChanged', handleClientChange);
      window.removeEventListener('genieMapChanged', handleGenieMapChanged);
    };
  // [fetchGenies]: stable useCallback ref — included to satisfy linter, effectively [] once.
  }, [fetchGenies]);

  const value = {
    genies,
    selectedGenie,
    // Expose the ref-synced setter so external callers (e.g. GenericChatPage handleGenieChange)
    // also keep selectedGenieRef up-to-date. This ensures that a user-initiated genie switch
    // is not overwritten by a subsequent fetchGenies re-fetch that reads the stale ref.
    setSelectedGenie: setSelectedGenieAndRef,
    isAuthenticated,
    currentUser,
    selectedClient,
    isLoading,
    refreshGenies: fetchGenies,
    // Processing flag — set to true by GenericChatPage while waiting for a response.
    // Navbar and the genie dropdown read this to disable client/genie switching.
    isProcessing,
    setIsProcessing,
  };

  // Always render children immediately, don't wait for data
  return (
    <GenieContext.Provider value={value}>
      {children}
    </GenieContext.Provider>
  );
};